import React from 'react';
import { DisplayMode } from '../helpers/content/contentInterfaces';
import ClusterSelectContent from '../activity/ClusterSelectContent';
import Content, { Rules } from '../helpers/content';
import { tempFlashbarMessage } from '../helpers';
import {
	CreateAdHocQueryRequest,
	PutAdHocQueryRequest,
} from '@amzn/aws-hammerstone-exposed-restful-service-typescript-client/clients/hammerstoneexposedrestfulservicelambda';
import { CreateAdHocQuery, PutAdHocQuery } from 'src/lib/hammerstoneApi';
import store, { actions } from 'src/data/redux';
import { SpaceBetween } from '@amzn/awsui-components-console';
import { isValidSelectQuery } from '../helpers/content/Rules';

export function AdHocContainerLayout(props: { queryId: string; mode: DisplayMode }) {
	const { queryId, mode } = props;
	const contentProps = {
		resourceType: 'adHocQuery',
		resourceId: queryId,
		mode,
		rules: (l: string) => ({
			required: Rules.required(l),
		}),
	} as const;

	return (
		<SpaceBetween size="m">
			<ClusterSelectContent
				{...contentProps}
				label="Cluster name"
				path="clusterName"
				access="READ"
				onlyShowEnabled
			/>
			<Content.Code
				{...contentProps}
				label="Select query"
				path="selectQuery"
				language="sql"
				rules={(l) => ({
					required: Rules.required(l),
					validate: {
						isValidSelectQuery: Rules.isValidSelectQuery(l),
					},
				})}
			/>
		</SpaceBetween>
	);
}

export async function createAdHocQueryWithFlashbar(request: CreateAdHocQueryRequest, shouldExecute?: boolean) {
	try {
		const { queryId } = await CreateAdHocQuery(request);
		tempFlashbarMessage(
			{
				id: `query_${queryId}_created`,
				message: {
					header: `Successfully created ad hoc query ${queryId}`,
					content: shouldExecute
						? 'Attempting to execute newly created query'
						: 'You may execute this query at a later time from the view query page',
				},
				persistAcrossApp: true,
			},
			5_000
		);
		if (shouldExecute) {
			await putAdHocQueryWithFlashbar({ ...request, queryId, body: { operation: 'execute' } });
		}
		return queryId;
	} catch (e) {
		console.error('Encountered error when creating ad hoc query with request', request, e);
		store.dispatch(
			actions.page.addToFlashbar({
				id: `create_ad_hoc_failure`,
				message: {
					type: 'error',
					header: `Failed to create ${shouldExecute ? 'and execute' : ''} ad hoc query`,
					content: e.message,
					dismissible: true,
				},
			})
		);
		return null;
	}
}

export async function putAdHocQueryWithFlashbar(request: PutAdHocQueryRequest) {
	try {
		const { queryId } = await PutAdHocQuery(request);
		tempFlashbarMessage(
			{
				id: `query_${queryId}_executed`,
				message: {
					header: `Successfully initiated ${request?.body?.operation} for ad hoc query ${queryId}`,
					content: 'Query status can be found from the view query page',
				},
				persistAcrossApp: true,
			},
			5_000
		);
		return queryId;
	} catch (e) {
		console.error('Encountered error when putting ad hoc query with request', request, e);
		store.dispatch(
			actions.page.addToFlashbar({
				id: `create_ad_hoc_failure`,
				message: {
					type: 'error',
					header: `Failed to execute ad hoc query`,
					content: e.message,
					dismissible: true,
				},
			})
		);
		return null;
	}
}
